<template>
  <CDropdown
    v-if="activeSchool"
    variant="nav-item"
    class="v3-sidebar-school-dropdown"
    placement="bottom-end"
    :class="{ 'no-dropdown': noSchoolOptions }"
  >
    <div
      v-if="isLoading"
      class="d-flex justify-content-center align-items-center my-1"
      role="status"
      aria-live="polite"
    >
      <span class="visually-hidden">{{ $t("common.a11y_loading") }}</span>
      <CSpinner color="primary" size="sm" aria-hidden="true" />
    </div>
    <CDropdownToggle
      data-test-id="sidebar-school-dropdown"
      component="button"
      :aria-label="schoolDropdownAriaLabel"
      :aria-busy="isLoading ? 'true' : undefined"
      :aria-disabled="noSchoolOptions ? 'true' : undefined"
      :aria-haspopup="noSchoolOptions ? undefined : 'menu'"
      @click="(e) => e.preventDefault()"
    >
      <div
        class="d-flex align-items-center"
        :class="{ 'cursor-default': noSchoolOptions }"
      >
        <div class="v3-avatar">
          <img
            :src="schoolLogoSrc"
            alt="School logo"
            @error="onSchoolLogoError"
          />
        </div>
        <div class="px-2 flex-1">
          <p class="m-0 font-size-12">{{ $t("common.school") }}</p>
          <b
            class="flex-grow-1 font-size-14 text-wrap text-break"
            data-test-id="sidebar-school-name"
            >{{ activeSchool.name }}</b
          >
        </div>
      </div>
    </CDropdownToggle>
    <CDropdownMenu v-if="!noSchoolOptions">
      <CDropdownItem
        v-for="(school, index) in schoolOptions"
        :key="index"
        class="px-2 cursor-pointer"
        @click="setActiveSchool(school)"
      >
        <div
          class="text-start school-name flex-grow-1 text-wrap"
          data-test-id="sidebar-school-list"
        >
          {{ school.name }}
        </div>
      </CDropdownItem>
    </CDropdownMenu>
  </CDropdown>
</template>

<script>
import { mapState } from "vuex"
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import { clearAdvanceReportingCache } from "@/store/modules/axiousWithCache"

export default {
  name: "TheHeaderDropdownAccnt",
  data() {
    return {
      isLoading: false,
      schoolSelectHeight: null,
      isSelectScrolable: false,
      schoolLogoLoadFailed: false,
      defaultSchoolLogo: "/img/avatars/school-avatar.png"
    }
  },
  computed: {
    ...mapState({
      schools: (state) => state.schools.schools,
      activeSchool: (state) => state.schools.activeSchool
    }),
    role() {
      return this.$store.getters["authentication/getUserRole"]
    },
    schoolOptions() {
      return this.schools && this.schools.length
        ? this.schools.filter((el) => el.id !== this.activeSchool.id)
        : []
    },
    noSchoolOptions() {
      return !this.schoolOptions.length
    },
    schoolDropdownAriaLabel() {
      if (!this.activeSchool) {
        return undefined
      }
      return `${this.$t("common.school")}: ${this.activeSchool.name}`
    },
    schoolLogoSrc() {
      if (this.schoolLogoLoadFailed || !this.activeSchool?.logo) {
        return this.defaultSchoolLogo
      }
      return this.activeSchool.logo
    }
  },
  watch: {
    "activeSchool.logo"() {
      this.schoolLogoLoadFailed = false
    }
  },
  mounted() {
    this.initSchoolsSelectSize()
  },
  methods: {
    onSchoolLogoError(event) {
      this.schoolLogoLoadFailed = true
      event.target.src = this.defaultSchoolLogo
    },
    async setActiveSchool(school) {
      if (school && school.name !== this.activeSchool.name) {
        try {
          this.isLoading = true
          await this.$store.dispatch("schools/changeActiveSchool", school)
          await this.$store.dispatch("authentication/getUserDetails", {
            clearCacheEntry: true
          })
          // const user = this.$store.getters["authentication/user"]

          await this.$store.dispatch("schools/getSchools", {
            clearCacheEntry: true
          })
          // const activeSchool = this.$store.getters["schools/activeSchool"]

          if (this.role === "student") {
            await this.$store.dispatch("schools/getActiveModules", {
              clearCacheEntry: true
            })
            await this.$store.dispatch("favorites/getFavorites", {
              clearCacheEntry: true
            })
          } else {
            await this.$store.dispatch("authentication/getUserSettings", {
              clearCacheEntry: true
            })
          }

          await clearAdvanceReportingCache()

          const userRole = this.$store.getters["authentication/getUserRole"]
          const isAdminRole = userRole === "admin" || userRole === "superadmin"

          if (isAdminRole) {
            window.location.replace(
              window.location.origin + advanceReportingConstants.PATHS.REPORT
            )
          } else {
            window.location.replace(window.location.origin + "/dashboard")
          }
        } catch (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Something went wrong while changing school"
          localStorage.setItem(`SchoolChangeEvent - ${school.name}`, message)
        } finally {
          this.isLoading = false
        }
      }
    },
    initSchoolsSelectSize() {
      const body = document.getElementsByClassName("main-scroll-container")[0]
      if (this.schoolOptions && body) {
        this.isSelectScrolable =
          this.schoolOptions.length * 77 + 100 > body.offsetHeight
      }
      this.schoolSelectHeight =
        body && this.schoolOptions && this.isSelectScrolable
          ? body.offsetHeight + "px"
          : "auto"
    }
  }
}
</script>
